import React from 'react';

/**
 * Vision Section
 *
 * Explains the data gap problem and why it matters.
 * Includes data flow diagrams showing current vs. proposed state.
 */
const VisionSection = ({ content }) => {
  return (
    <div className="df-vision">
      <header className="df-section__header">
        <h2 className="df-section__title">{content.title}</h2>
        <p className="df-section__subtitle">{content.subtitle}</p>
      </header>

      {/* Problem Statement */}
      <div className="df-problem">
        <h3 className="df-flow-diagram__title">{content.problem.headline}</h3>
        <p className="df-gap-card__description" style={{ marginBottom: '2rem' }}>
          {content.problem.description}
        </p>

        {/* Current Data Flow */}
        <DataFlowDiagram
          title="Current Data Flow"
          steps={content.problem.currentFlow}
          variant="current"
        />

        {/* Proposed Data Flow */}
        <DataFlowDiagram
          title="Proposed Data Flow"
          steps={content.problem.proposedFlow}
          variant="proposed"
        />
      </div>

      {/* Data Gaps */}
      <div className="df-gaps" style={{ marginTop: '3rem' }}>
        <h3 className="df-flow-diagram__title">Identified Data Gaps</h3>
        <div className="df-gaps-grid">
          {content.gaps.map((gap) => (
            <GapCard key={gap.id} gap={gap} />
          ))}
        </div>
      </div>

      {/* Why It Matters */}
      <div className="df-impact" style={{ marginTop: '3rem' }}>
        <h3 className="df-flow-diagram__title">Why This Matters</h3>
        <div className="df-impact-grid">
          {content.whyItMatters.map((item, index) => (
            <ImpactCard key={index} item={item} />
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Data Flow Diagram Component
 */
const DataFlowDiagram = ({ title, steps, variant }) => {
  return (
    <div className="df-flow-diagram">
      <h4 className="df-flow-diagram__title" style={{ fontSize: '0.9rem', color: variant === 'proposed' ? '#1F93FE' : '#64748B' }}>
        {title}
      </h4>
      <div className="df-flow-steps">
        {steps.map((step, index) => (
          <React.Fragment key={step.step}>
            <div className={`df-flow-step df-flow-step--${step.status}`}>
              <div className="df-flow-step__icon">
                <FlowIcon name={step.icon} />
              </div>
              <span className="df-flow-step__label">{step.label}</span>
            </div>
            {index < steps.length - 1 && (
              <span className="df-flow-arrow">→</span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

/**
 * Gap Card Component
 */
const GapCard = ({ gap }) => {
  return (
    <div className={`df-gap-card df-gap-card--${gap.severity}`}>
      <div className="df-gap-card__icon">
        <GapIcon name={gap.icon} />
      </div>
      <div className="df-gap-card__content">
        <h4 className="df-gap-card__title">
          {gap.title}
          <span className={`df-gap-card__severity df-gap-card__severity--${gap.severity}`}>
            {gap.severity}
          </span>
        </h4>
        <p className="df-gap-card__description">{gap.description}</p>
        <p className="df-gap-card__estimate">
          <strong>Estimated impact:</strong> {gap.estimatedData}
        </p>
      </div>
    </div>
  );
};

/**
 * Impact Card Component
 */
const ImpactCard = ({ item }) => {
  return (
    <div className="df-impact-card">
      <div className="df-impact-card__icon">
        <ImpactIcon name={item.icon} />
      </div>
      <h4 className="df-impact-card__title">{item.title}</h4>
      <p className="df-impact-card__description">{item.description}</p>
    </div>
  );
};

/**
 * Flow Step Icons
 */
const FlowIcon = ({ name }) => {
  const icons = {
    video: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
        <polygon points="23 7 16 12 23 17 23 7" />
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
      </svg>
    ),
    'file-text': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    ),
    cloud: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
        <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
      </svg>
    ),
    'x-circle': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
        <circle cx="12" cy="12" r="10" />
        <line x1="15" y1="9" x2="9" y2="15" />
        <line x1="9" y1="9" x2="15" y2="15" />
      </svg>
    ),
    brain: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
        <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z" />
        <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z" />
      </svg>
    ),
    'refresh-cw': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
        <polyline points="23 4 23 10 17 10" />
        <polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
      </svg>
    ),
    cpu: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
        <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
        <rect x="9" y="9" width="6" height="6" />
        <line x1="9" y1="1" x2="9" y2="4" />
        <line x1="15" y1="1" x2="15" y2="4" />
        <line x1="9" y1="20" x2="9" y2="23" />
        <line x1="15" y1="20" x2="15" y2="23" />
      </svg>
    ),
    lightbulb: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
        <line x1="9" y1="18" x2="15" y2="18" />
        <line x1="10" y1="22" x2="14" y2="22" />
        <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
      </svg>
    )
  };

  return icons[name] || <span>?</span>;
};

/**
 * Gap Card Icons
 */
const GapIcon = ({ name }) => {
  const icons = {
    'hard-drive': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
        <line x1="22" y1="12" x2="2" y2="12" />
        <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
        <line x1="6" y1="16" x2="6.01" y2="16" />
        <line x1="10" y1="16" x2="10.01" y2="16" />
      </svg>
    ),
    'link-2': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
        <path d="M15 7h3a5 5 0 0 1 5 5 5 5 0 0 1-5 5h-3m-6 0H6a5 5 0 0 1-5-5 5 5 0 0 1 5-5h3" />
        <line x1="8" y1="12" x2="16" y2="12" />
      </svg>
    ),
    'file-search': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <circle cx="11.5" cy="14.5" r="2.5" />
        <line x1="13.25" y1="16.25" x2="15" y2="18" />
      </svg>
    )
  };

  return icons[name] || <span>?</span>;
};

/**
 * Impact Card Icons
 */
const ImpactIcon = ({ name }) => {
  const icons = {
    'trending-up': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
      </svg>
    ),
    'graduation-cap': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28">
        <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
        <path d="M6 12v5c3 3 9 3 12 0v-5" />
      </svg>
    ),
    brain: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28">
        <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z" />
        <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z" />
      </svg>
    ),
    'bar-chart-2': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    )
  };

  return icons[name] || <span>?</span>;
};

export default VisionSection;
